import { PusherService } from './../pusher/pusher-service';
import { Injectable, Injector } from '@angular/core';
import * as _ from 'lodash';
import { ModalsService } from '../../modals/modals.service';
import { PortalConfig } from '../../services/portal-config';

/**
 * Inner class that manages the interaction with Pusher and the HTTP endpoint.
 */

@Injectable()
export class BackgroundJob {
  constructor(private portalConfig: PortalConfig, private injector: Injector) {
    let backgroundJobConstructor: any;
    backgroundJobConstructor = uuid => {
      const backgroundJob = initBackgroundJob(this.portalConfig, this.injector);
      return new backgroundJob(uuid);
    };
    return backgroundJobConstructor;
  }
}

/* istanbul ignore next */
function initBackgroundJob(portalConfig, injector) {
  // tslint:disable-next-line:max-classes-per-file
  return class BackgroundJob {
    _uuid;
    _channel;
    _pusherHandler;
    _deferred;
    _hasOutstandingPollRequest;
    _pollPromise;
    _isPolling;
    private publicConfig: PortalConfig['publicConfig'];
    private injected$q;
    private injected$Http;
    private injectedPusher: PusherService;
    private injected$Interval;
    private injectedErrorModal;
    private injectedModalsService;

    constructor(uuid) {
      this.injected$q = injector.get('$q');
      this.injected$Http = injector.get('$http');
      this.injected$Interval = injector.get('$interval');
      this.injectedPusher = injector.get('PusherService');
      this.injectedErrorModal = injector.get('ErrorModal');
      this.injectedModalsService = injector.get(ModalsService);

      this.publicConfig = portalConfig.publicConfig;
      this._uuid = uuid;
      this._channel = this.injectedPusher.pusher.subscribe('nv-background-job-' + this._uuid);
      this._pusherHandler = this._handlePusherMessage.bind(this);
      this._channel.bind('status-update', this._pusherHandler);
      this._deferred = this.injected$q.defer();
      this._hasOutstandingPollRequest = false;

      this._startPolling();
    }

    static get POLLING_INTERVAL() {
      return 3000;
    } // milliseconds

    getById(id) {
      return this.injected$Http.get(this.publicConfig.NV_API_ORIGIN + '/v1/backgroundJobs/' + id);
    }

    _startPolling() {
      this._pollPromise = this.injected$Interval(this._poll.bind(this), BackgroundJob.POLLING_INTERVAL);
      this._isPolling = true;
    }

    _stopPolling() {
      this.injected$Interval.cancel(this._pollPromise);
      this._isPolling = false;
    }

    _poll() {
      // Don't make another request if we are still waiting for one to resolve
      if (this._hasOutstandingPollRequest) return;

      this.getById(this._uuid).then(response => {
        if (!this._isPolling) return; // in case requests come in after we stop polling, ignore them

        let type;
        switch (response.data.state) {
          case 'STARTED':
            type = 'NOTIFY';
            break;
          case 'RESOLVED':
            type = 'RESOLVE';
            break;
          case 'REJECTED':
            type = 'REJECT';
            break;
          case 'PARTIAL_SETTLED':
            type = 'PARTIAL';
            break;
          default:
            return;
        }

        this._handleStatusUpdate({
          type,
          status: response.data.status,
          response,
        });

        this._hasOutstandingPollRequest = false;
      });

      this._hasOutstandingPollRequest = true;
    }

    _handlePusherMessage(msg) {
      this._handleStatusUpdate(msg);
    }

    _stopListeningToPusher() {
      this._channel.unbind('status-update', this._pusherHandler);
    }

    _handleStatusUpdate(data) {
      switch (data.type) {
        case 'NOTIFY':
          this._deferred.notify(data.status);
          break;
        case 'RESOLVE':
          this.resolve();
          break;
        case 'REJECT':
          this._deferred.reject(data.status);
          this._showRejectError(data);
          this._stopListeningToPusher();
          this._stopPolling();
          break;
        case 'PARTIAL':
          this._deferred.resolve(data);
          this._showPartialErrors(data);
          this._stopListeningToPusher();
          this._stopPolling();
          break;
        default:
          break;
      }
    }

    _formatPartialErrors(errors) {
      const acc = {
        message: '',
        details: '',
      };

      _.reduce(
        errors,
        (acc, docs: any, errMssg) => {
          const docsInfo = {
            humanReadable: [],
            ids: [],
          };

          _.reduce(
            docs,
            (acc, v: any) => {
              acc.humanReadable.push(v.humanReadableIdentifier);
              acc.ids.push(v.id);
              return acc;
            },
            docsInfo,
          );

          let wasOrWere = docs.length > 1 ? 'were' : 'was';
          let formattedMssg = errMssg.replace(/_/g, '.');

          formattedMssg = formattedMssg.replace(/\\/g, '');

          acc.message += `${
            docsInfo.humanReadable
          } - ${wasOrWere} not processed due to the following error: ${formattedMssg}\n`;

          acc.details += `_ids: ${
            docsInfo.ids
          } - ${wasOrWere} not processed due to the following error: ${formattedMssg}\n`;

          return acc;
        },
        acc,
      );

      return acc;
    }

    _showPartialErrors(data) {
      const errors = data.status.errors;
      let formattedErrors = this._formatPartialErrors(errors);

      if (errors) {
        this.injectedErrorModal.showAndRequireRefresh('Error: The following requests could not be processed:', {
          message: formattedErrors.message,
          details: formattedErrors.details,
        });
      }
    }

    _showRejectError(data) {
      const error = data.status.error;
      if (error) {
        const payload = this._constructErrorPayload(data);
        this.injectedModalsService.openErrorModal(payload);
      }
    }

    _constructErrorPayload(data) {
      const {
        status: { message },
        response: {
          data: { className },
        },
      } = data;
      const response = {
        title: 'Unable to complete batch action',
        message,
        confirmText: 'Try again',
      };
      return response;
    }

    resolve(data?) {
      this._stopListeningToPusher();
      this._stopPolling();
      return this._deferred.resolve(data);
    }

    promise() {
      return this._deferred.promise;
    }
  };
}
